import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { UseFormReturn } from "react-hook-form";
import { useBillCycleOptions } from "../hooks";

interface SingleBillFiltersType {
  status: string;
  bill_cycle: string;
  billing_month: string;
  from: string;
  to: string;
  page: number;
}

interface SingleBillFiltersProps {
  filters: SingleBillFiltersType;
  onFiltersChange: (filters: SingleBillFiltersType) => void;
  onApplyFilters: (filters: SingleBillFiltersType) => void;
  months: any[];
  onClearFilters: () => void;
}

export const SingleBillFilters: React.FC<SingleBillFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  months,
  onClearFilters,
}) => {
  const { data: billCycleOptions } = useBillCycleOptions();
  const FIELD_CLASSES = {
    container: "w-full mb-4",
    label: "block text-sm font-semibold text-gray-700 mb-2",
    input:
      "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    select:
      "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    textarea:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    error: "text-red-600 text-sm mt-1",
    helperText: "text-xs text-gray-500 mt-1",
  } as const;

  const createFormFields = (): FormField[] => [
    {
      name: "status",
      label: "Status",
      type: "select",
      required: false,
      placeholder: "All Status",
      options: [
        { value: "APPROVED", label: "Approved" },
        { value: "PENDING", label: "Pending" },
      ],
      classes: FIELD_CLASSES,
    },
    {
      name: "billing_month", // Fixed: changed from billing_month to billingMonth
      label: "Billing Month",
      type: "select",
      required: false,
      placeholder: "All Months",
      options: [
        { label: "All Months", value: "all" },
        ...months.map((month) => ({
          label: month.label,
          value: month.value,
        })),
      ],
      classes: FIELD_CLASSES,
    },
    {
      name: "created_on_start", // Fixed: changed from date_from to dateFrom
      label: "Generated Date From",
      type: "datepicker",
      required: false,
      classes: FIELD_CLASSES,
    },
    {
      name: "created_on_end", // Fixed: changed from date_to to dateTo
      label: "Generated Date To",
      type: "datepicker",
      required: false,
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form: UseFormReturn<SingleBillFiltersType> =
    FormService.createForm<SingleBillFiltersType>({
      fields: createFormFields(),
      defaultValues: filters,
    });

  // Handle form submission (Apply Filters)
  const handleSubmit = (data: SingleBillFiltersType) => {
    const currentValues = form.getValues();
    onApplyFilters({ ...currentValues, page: 1 }); // Reset page when applying filters
  };

  // Handle form reset (Clear Filters)
  const handleReset = () => {
    const defaultFilters: SingleBillFiltersType = {
      status: "",
      bill_cycle: "",
      billing_month: "",
      from: "",
      to: "",
      page: 1,
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  // Custom footer with Apply and Clear buttons
  const renderCustomFooter = () => (
    <div className="col-span-1 md:col-span-2 lg:col-span-5 flex justify-end gap-2 pt-6 border-gray-200">
      <button
        type="button"
        onClick={handleReset}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      >
        Clear Filters
      </button>

      <button
        type="submit"
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Apply Filters
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false, // We're using custom footer
          showResetButton: false, // We're using custom footer
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={4}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
